import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { AlertCircle, RefreshCw, XCircle, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

type Alert = {
  id: string;
  bot_id: string;
  signal: string;
  payload: any;
  processed: boolean;
  exchange_confirmed: boolean;
  created_at: string;
  trading_bots?: {
    name: string | null;
  } | null;
  error_logs?: Array<{
    id: string;
    error_type: string;
    error_message: string;
    error_details: any;
    created_at: string;
  }> | null;
};

export default function AlertsMonitoring() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAlerts();
    // Set up real-time subscription
    const subscription = supabase
      .channel('alerts_and_errors')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bot_alerts' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            toast.success('New alert received!');
          }
          loadAlerts();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadAlerts = async () => {
    try {
      setRefreshing(true);
      
      // Load alerts with related bot names
      const { data: alertsData, error: alertsError } = await supabase
        .from('bot_alerts')
        .select(`
          id,
          bot_id,
          signal,
          payload,
          processed,
          exchange_confirmed,
          created_at,
          trading_bots (
            name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (alertsError) throw alertsError;

      // Load error logs for each alert
      const alertIds = alertsData?.map(alert => alert.id) || [];
      const { data: errorLogsData, error: errorLogsError } = await supabase
        .from('error_logs')
        .select('*')
        .in('alert_id', alertIds);

      if (errorLogsError) throw errorLogsError;

      // Combine alerts with their error logs
      const alertsWithErrors = alertsData?.map(alert => ({
        ...alert,
        error_logs: errorLogsData?.filter(log => log.alert_id === alert.id) || []
      })) || [];

      setAlerts(alertsWithErrors);
    } catch (error: any) {
      console.error('Error loading alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadAlerts();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
          <AlertCircle className="h-6 w-6" />
          <span>Alerts & Errors Monitoring</span>
        </h2>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className={`p-2 rounded-full hover:bg-blue-800/50 transition-colors ${
            refreshing ? 'animate-spin' : ''
          }`}
        >
          <RefreshCw className="h-5 w-5 text-gray-400" />
        </button>
      </div>

      {/* Alert Status Legend */}
      <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-4 border border-blue-500/20">
        <h3 className="text-lg font-medium text-white mb-4">Alert Status Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-yellow-400 animate-pulse" />
            <div>
              <p className="text-white font-medium">New Alert</p>
              <p className="text-sm text-gray-400">Alert just received, pending processing</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="h-5 w-5 text-green-400" />
            <div>
              <p className="text-white font-medium">Processed</p>
              <p className="text-sm text-gray-400">Alert has been processed by the system</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-5 w-5 text-cyan-400" />
            <div>
              <p className="text-white font-medium">Exchange Confirmed</p>
              <p className="text-sm text-gray-400">Order confirmed by the exchange</p>
            </div>
          </div>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="text-center py-8">
          <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400">No alerts yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`bg-blue-900/40 backdrop-blur-sm rounded-lg p-4 border ${
                !alert.processed 
                  ? 'border-yellow-500/20' 
                  : alert.exchange_confirmed 
                    ? 'border-cyan-500/20'
                    : 'border-green-500/20'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  {!alert.processed ? (
                    <AlertCircle className="h-5 w-5 text-yellow-400 animate-pulse" />
                  ) : alert.exchange_confirmed ? (
                    <AlertTriangle className="h-5 w-5 text-cyan-400" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                  )}
                  <div>
                    <p className="text-white font-medium">
                      {alert.trading_bots?.name || 'Unknown Bot'}
                    </p>
                    <p className="text-sm text-gray-400">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {!alert.processed && (
                    <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-400 animate-pulse">
                      New Alert
                    </span>
                  )}
                  {alert.processed && (
                    <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400">
                      Processed
                    </span>
                  )}
                  {alert.exchange_confirmed && (
                    <span className="px-2 py-1 text-xs rounded-full bg-cyan-500/20 text-cyan-400">
                      Exchange Confirmed
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Signal Details</h4>
                  <div className="bg-black/20 rounded p-3">
                    <p className="text-sm text-gray-300">
                      <span className="font-medium">Signal:</span> {alert.signal}
                    </p>
                    <p className="text-sm text-gray-300">
                      <span className="font-medium">Symbol:</span> {alert.payload?.symbol || alert.payload?.ticker}
                    </p>
                    <p className="text-sm text-gray-300">
                      <span className="font-medium">Price:</span> {alert.payload?.price}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Full Payload</h4>
                  <div className="bg-black/20 rounded p-3">
                    <pre className="text-xs text-gray-300 overflow-x-auto">
                      {JSON.stringify(alert.payload, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>

              {alert.error_logs && alert.error_logs.length > 0 && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded">
                  <div className="flex items-center space-x-2 mb-2">
                    <XCircle className="h-4 w-4 text-red-400" />
                    <p className="text-sm font-medium text-red-400">Errors</p>
                  </div>
                  <div className="space-y-2">
                    {alert.error_logs.map((error) => (
                      <div key={error.id} className="bg-black/20 rounded p-2">
                        <p className="text-sm text-red-300 font-medium">{error.error_type}</p>
                        <p className="text-sm text-red-300">{error.error_message}</p>
                        <pre className="text-xs text-red-300 mt-1 overflow-x-auto">
                          {JSON.stringify(error.error_details, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
